import { useEffect, useState, useRef } from "react";
import { DataTable } from "../../DataTable/DataTable";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import { InverntroyService } from "../../../services/api/inventroy.service";
import { OrderService } from "../../../services/api/order.service";
import avatarPlaceholder from "../../../assets/images/default_avatar.jpg";
import { SearchableDropdown } from "../../../shared/SearchableDropdown";

interface MarketerOption {
  id: string;
  marketerName: string;
}

interface ManufacturerOption {
  id: string;
  manufacturerName: string;
}

interface ClinicOption {
  id: string;
  name: string;
}

interface StockReportItem {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  purchaseDate: string;
  productName: string;
  productImage: string;
  manufacturerName: string;
  marketerName: string;
  totalQuantity: number;
}

interface Filters {
  marketerId: string;
  manufacturerId: string;
  fromDate: string;
  toDate: string;
  productName: string;
  clinicId: string;
}

const StockReport = () => {
  const { user } = useAuth();
  
  // Get date range: 1 month ago to today
  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);

  const [filters, setFilters] = useState<Filters>({
    marketerId: "",
    manufacturerId: "",
    fromDate: formatDate(lastMonth),
    toDate: formatDate(today),
    productName: "",
    clinicId: user?.role === "clinic" ? user?.id || "" : "",
  });

  const [data, setData] = useState<StockReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [marketerOptions, setMarketerOptions] = useState<MarketerOption[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<ManufacturerOption[]>([]);
  const [clinicList, setClinicList] = useState<ClinicOption[]>([]);
  
  const [manufacturerSearchText, setManufacturerSearchText] = useState("");
  const [marketerSearchText, setMarketerSearchText] = useState("");
  const [showManufacturerSuggestions, setShowManufacturerSuggestions] = useState(false);
  const [showMarketerSuggestions, setShowMarketerSuggestions] = useState(false);

  const manufacturerDropdownRef = useRef<HTMLDivElement>(null);
  const marketerDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        manufacturerDropdownRef.current &&
        !manufacturerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowManufacturerSuggestions(false);
      }
      if (
        marketerDropdownRef.current &&
        !marketerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMarketerSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const fetchOptions = async () => {
    try {
      const marketers = await InverntroyService.getMarketerOptions();
      const manufacturers = await InverntroyService.getManufacturerOptions();
      const clinicsRes = await OrderService.getClinicList();
      setMarketerOptions(marketers || []);
      setManufacturerOptions(manufacturers || []);
      if (clinicsRes && clinicsRes.data) {
        setClinicList(clinicsRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch options", error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await InverntroyService.getStockReport({
        ...filters,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      if (res.success) {
        setData(res.data);
        setPagination((prev) => ({
          ...prev,
          totalItems: res.pagination.totalItems,
          totalPages: res.pagination.totalPages,
        }));
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Failed to fetch stock report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [filters, pagination.page, pagination.pageSize]);

  const handleExportToExcel = async () => {
    setDownloading(true);
    try {
      let allData: StockReportItem[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const res = await InverntroyService.getStockReport({
          ...filters,
          page: currentPage,
          pageSize: 50,
        });
        if (res.success && res.data.length > 0) {
          allData = [...allData, ...res.data];
          if (res.data.length < 50) hasMore = false;
          else currentPage++;
        } else {
          hasMore = false;
        }
      }

      if (allData.length === 0) {
        toast.error("No data to export");
        return;
      }

      const formattedData = allData.map((item) => ({
        "Invoice Number": item.invoiceNumber,
        "Invoice Date": item.invoiceDate ? new Date(item.invoiceDate).toLocaleDateString() : "N/A",
        "Purchase Date": item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : "N/A",
        "Product Name": item.productName,
        "Manufacturer": item.manufacturerName,
        "Marketer": item.marketerName,
        "Total Quantity": item.totalQuantity,
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
      XLSX.writeFile(workbook, `stock_report_${formatDate(new Date())}.xlsx`);
      toast.success("Report downloaded successfully");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      key: "productName",
      title: "Product Name",
      render: (item: StockReportItem) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg overflow-hidden border flex-shrink-0">
            <img
              src={item.productImage || avatarPlaceholder}
              alt={item.productName}
              className="w-full h-full object-cover"
            />
          </div>
          <span className="font-medium text-gray-900">{item.productName}</span>
        </div>
      ),
    },
    {
      key: "invoiceNumber",
      title: "Invoice No",
      render: (item: StockReportItem) => <span>{item.invoiceNumber || "N/A"}</span>,
    },
    {
      key: "invoiceDate",
      title: "Invoice Date",
      render: (item: StockReportItem) => (
        <span>{item.invoiceDate ? new Date(item.invoiceDate).toLocaleDateString() : "N/A"}</span>
      ),
    },
    {
      key: "purchaseDate",
      title: "Purchase Date",
      render: (item: StockReportItem) => (
        <span>{item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : "N/A"}</span>
      ),
    },
    {
      key: "manufacturerName",
      title: "Manufacturer",
      render: (item: StockReportItem) => <span>{item.manufacturerName || "N/A"}</span>,
    },
    {
      key: "marketerName",
      title: "Marketer",
      render: (item: StockReportItem) => <span>{item.marketerName || "N/A"}</span>,
    },
    {
      key: "totalQuantity",
      title: "Total Quantity",
      render: (item: StockReportItem) => <span className="font-bold text-emerald-600">{item.totalQuantity}</span>,
    },
  ];

  return (
    <div className="space-y-4 mt-4">
      <div className={`grid grid-cols-1 gap-4 bg-white p-4 rounded-lg border shadow-sm ${user?.role === "clinic" ? "md:grid-cols-3 lg:grid-cols-6" : "md:grid-cols-4 lg:grid-cols-7"}`}>
        <input
          type="text"
          placeholder="Product Name"
          className="border rounded px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
          value={filters.productName}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, productName: e.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        />

        {user?.role !== "clinic" && (
          <select
            value={filters.clinicId}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, clinicId: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="border rounded px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm text-gray-700 bg-white"
          >
            <option value="">All Clinics</option>
            {clinicList.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </select>
        )}

        <SearchableDropdown
          placeholder="All Manufacturers"
          searchText={manufacturerSearchText}
          setSearchText={(val) => {
            setManufacturerSearchText(val);
            setFilters((prev) => ({ ...prev, manufacturerId: "" }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          suggestionsVisible={showManufacturerSuggestions}
          setSuggestionsVisible={setShowManufacturerSuggestions}
          options={(manufacturerOptions || []).map((m) => ({
            id: m.id,
            label: m.manufacturerName,
          }))}
          onSelect={(selected) => {
            setFilters((prev) => ({ ...prev, manufacturerId: selected.id }));
            setManufacturerSearchText(selected.label);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          dropdownRef={manufacturerDropdownRef}
        />

        <SearchableDropdown
          placeholder="All Marketers"
          searchText={marketerSearchText}
          setSearchText={(val) => {
            setMarketerSearchText(val);
            setFilters((prev) => ({ ...prev, marketerId: "" }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          suggestionsVisible={showMarketerSuggestions}
          setSuggestionsVisible={setShowMarketerSuggestions}
          options={(marketerOptions || []).map((m) => ({
            id: m.id,
            label: m.marketerName,
          }))}
          onSelect={(selected) => {
            setFilters((prev) => ({ ...prev, marketerId: selected.id }));
            setMarketerSearchText(selected.label);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          dropdownRef={marketerDropdownRef}
        />

        <input
          type="date"
          className="border rounded px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
          value={filters.fromDate}
          onChange={(e) => {
            setFilters({ ...filters, fromDate: e.target.value });
            setPagination({ ...pagination, page: 1 });
          }}
        />

        <input
          type="date"
          className="border rounded px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
          value={filters.toDate}
          onChange={(e) => {
            setFilters({ ...filters, toDate: e.target.value });
            setPagination({ ...pagination, page: 1 });
          }}
        />

        <button
          onClick={handleExportToExcel}
          disabled={downloading}
          className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 transition-colors disabled:bg-gray-400 flex items-center justify-center space-x-2 text-sm"
        >
          {downloading ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      <DataTable
        data={data}
        columns={columns}
        isLoading={loading}
        pagination={{
          currentPage: pagination.page,
          setCurrentPage: (page) => setPagination({ ...pagination, page }),
          totalPages: pagination.totalPages,
          totalItemsCount: pagination.totalItems,
          pageSize: pagination.pageSize,
          setPageSize: (pageSize) => setPagination({ ...pagination, pageSize, page: 1 }),
        }}
      />
    </div>
  );
};

export default StockReport;
